/**
 * Jaipur Handloom AE8 component owner
 * File: jh-40-performance-cart-base.js
 * Owns: merchandising pools, hero randomization, non-visual image tuning,
 *       accessible scroll regions, save-cart dialog and hero quick add.
 * Depends on: jh-00-core.js; uses jh-90-cart.js at interaction time when available.
 * Do not add a second cart renderer, image pass or carousel sorter here.
 */
(function() {
  'use strict';

  var JH = window.JH = window.JH || {};
  var U = JH.Utils;
  var A11y = JH.A11y;
  var state = {
    saveCartBound: false,
    heroAddBound: false,
    saveCartOpener: null
  };

  function qs(selector, root) {
    return U ? U.qs(selector, root) : (root || document).querySelector(selector);
  }

  function qsa(selector, root) {
    return U ? U.qsa(selector, root) : Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function escapeHtml(value) {
    return U ? U.escapeHtml(value) : String(value == null ? '' : value).replace(/[&<>"']/g, '');
  }

  function numberAttribute(element, name) {
    var number = parseInt(element.getAttribute(name) || '0', 10);
    return isNaN(number) ? 0 : number;
  }

  function shuffle(items) {
    for (var index = items.length - 1; index > 0; index -= 1) {
      var randomIndex = Math.floor(Math.random() * (index + 1));
      var temporary = items[index];
      items[index] = items[randomIndex];
      items[randomIndex] = temporary;
    }
    return items;
  }

  function popularityScore(card) {
    return numberAttribute(card, 'data-jh-sold-count') * 1000 +
      numberAttribute(card, 'data-jh-review-count') * 10 +
      Math.floor(numberAttribute(card, 'data-jh-published-at') / 1000000000);
  }

  function trendingScore(card) {
    return numberAttribute(card, 'data-jh-sold-count') * 5 +
      numberAttribute(card, 'data-jh-review-count');
  }

  function orderPool(cards, purpose) {
    var ordered = cards.slice();
    var hasSales = cards.some(function(card) { return numberAttribute(card, 'data-jh-sold-count') > 0; });
    var hasPopularity = cards.some(function(card) { return popularityScore(card) > 0; });
    var hasTrending = cards.some(function(card) { return trendingScore(card) > 0; });
    var hasDates = cards.some(function(card) { return numberAttribute(card, 'data-jh-published-at') > 0; });

    if (purpose === 'best-seller' || purpose === 'most-popular') {
      if (hasSales || hasPopularity) {
        ordered.sort(function(a, b) {
          return popularityScore(b) - popularityScore(a) ||
            numberAttribute(a, 'data-jh-pool-index') - numberAttribute(b, 'data-jh-pool-index');
        });
      } else shuffle(ordered);
    } else if (purpose === 'new-arrivals') {
      if (hasDates) {
        ordered.sort(function(a, b) {
          return numberAttribute(b, 'data-jh-published-at') - numberAttribute(a, 'data-jh-published-at') ||
            numberAttribute(a, 'data-jh-pool-index') - numberAttribute(b, 'data-jh-pool-index');
        });
      } else shuffle(ordered);
    } else if (purpose === 'trending' || purpose === 'also-bought') {
      if (hasTrending) {
        ordered.sort(function(a, b) {
          return trendingScore(b) - trendingScore(a) ||
            numberAttribute(a, 'data-jh-pool-index') - numberAttribute(b, 'data-jh-pool-index');
        });
      } else shuffle(ordered);
    } else {
      shuffle(ordered);
    }
    return ordered;
  }

  function applyPool(row, purpose, limit) {
    if (!row || row.dataset.jhPoolReady === 'true') return;
    var cards = qsa('[data-jh-product-card]', row);
    cards.forEach(function(card, index) { card.dataset.jhPoolIndex = String(index); });
    orderPool(cards, purpose).forEach(function(card, index) {
      row.appendChild(card);
      card.classList.toggle('is-v1316-hidden', index >= limit);
      card.classList.remove('jh-v1316-hidden');
    });
    row.classList.add('is-v1316-ready');
    row.dataset.jhPoolReady = 'true';
  }

  function initPools(scope) {
    qsa('.jh-rug-pool-carousel-v1316[data-jh-carousel-purpose]', scope || document).forEach(function(section) {
      var limit = parseInt(section.getAttribute('data-jh-pool-limit') || section.getAttribute('data-jh-carousel-expected') || '25', 10) || 25;
      applyPool(qs('[data-jh-scroll-row]', section), section.getAttribute('data-jh-carousel-purpose') || 'featured', limit);
    });
    qsa('[data-jh-reco-v1316]', scope || document).forEach(function(section) {
      var limit = parseInt(section.getAttribute('data-jh-pool-limit') || '25', 10) || 25;
      applyPool(qs('[data-jh-reco-row]', section) || qs('[data-jh-scroll-row]', section), section.getAttribute('data-jh-reco-purpose') || 'recommended', limit);
    });
  }

  function initHeroRandomization(scope) {
    qsa('[data-jh-hero-gallery-v13134="true"]', scope || document).forEach(function(root) {
      var row = qs('[data-jh-hero-gallery-track]', root);
      if (!row || row.dataset.jhHeroRandomReady === 'true') return;
      var slides = qsa('[data-jh-hero-slide]', row);
      if (slides.length > 1) shuffle(slides).forEach(function(slide) { row.appendChild(slide); });
      row.dataset.jhHeroRandomReady = 'true';
    });
  }

  function makeRowsAccessible(scope) {
    qsa('[data-jh-scroll-row], [data-jh-reco-row], [data-jh-hero-gallery-track]', scope || document).forEach(function(row) {
      if (row.dataset.jhAccessibleRow === 'true') return;
      row.dataset.jhAccessibleRow = 'true';
      if (!row.hasAttribute('tabindex')) row.setAttribute('tabindex', '0');
      if (!row.hasAttribute('role')) row.setAttribute('role', 'region');
      if (!row.hasAttribute('aria-label')) row.setAttribute('aria-label', 'Scrollable product list');
      row.addEventListener('keydown', function(event) {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        row.scrollBy({ left: event.key === 'ArrowRight' ? 260 : -260, behavior: 'smooth' });
        event.preventDefault();
      });
    });
  }

  function tuneImages(scope) {
    var images = qsa('img', scope || document);
    var critical = qs('.jh-main-media img, [data-jh-hero-gallery-track] img, .jh-home-hero-v1314 img', scope || document);
    if (critical) {
      critical.setAttribute('loading', 'eager');
      critical.setAttribute('fetchpriority', 'high');
      critical.setAttribute('decoding', 'async');
    }
    images.forEach(function(image, index) {
      if (!image.hasAttribute('decoding')) image.setAttribute('decoding', 'async');
      if (image !== critical && index > 1 && !image.hasAttribute('loading')) image.setAttribute('loading', 'lazy');
      if (image !== critical && index > 3 && !image.hasAttribute('fetchpriority')) image.setAttribute('fetchpriority', 'low');
      if (!image.getAttribute('alt')) {
        var card = image.closest('[data-jh-product-card]');
        var title = card && qs('.jh-card__title', card);
        if (title) image.setAttribute('alt', title.textContent.trim());
      }
    });
  }

  function trimHiddenPools(scope) {
    qsa('[data-jh-scroll-row].is-v1316-ready,[data-jh-reco-row].is-v1316-ready', scope || document).forEach(function(row) {
      if (row.dataset.jhPoolTrimmed === 'true') return;
      var cards = qsa('[data-jh-product-card]', row);
      var keep = row.closest('[data-jh-reco-purpose="frequently-bought"]') ? 4 : 32;
      if (cards.length > keep) {
        cards.slice(keep).forEach(function(card) {
          if (card.classList.contains('is-v1316-hidden')) card.remove();
        });
      }
      row.dataset.jhPoolTrimmed = 'true';
    });
    qsa('[data-jh-hero-gallery-track]', scope || document).forEach(function(row) {
      if (row.dataset.jhHeroTrimmed === 'true') return;
      qsa('[data-jh-hero-slide]', row).slice(36).forEach(function(slide) { slide.remove(); });
      row.dataset.jhHeroTrimmed = 'true';
    });
  }

  function improveLoadMore(scope) {
    qsa('a[data-jh-load-more]', scope || document).forEach(function(link) {
      if (link.dataset.jhLoadMoreReady === 'true') return;
      link.dataset.jhLoadMoreReady = 'true';
      link.textContent = 'Load more products';
      link.setAttribute('aria-label', 'Load more products from this collection');
      function prefetch() {
        if (link.dataset.jhPrefetched === 'true') return;
        link.dataset.jhPrefetched = 'true';
        var resource = document.createElement('link');
        resource.rel = 'prefetch';
        resource.href = link.href;
        document.head.appendChild(resource);
      }
      link.addEventListener('mouseenter', prefetch, { once: true });
      link.addEventListener('touchstart', prefetch, { once: true, passive: true });
    });
  }

  function updateSaveCartSummary(cart) {
    var modal = qs('[data-jh-save-cart-modal]');
    var summary = qs('[data-jh-save-cart-summary]');
    var body = qs('[data-jh-save-cart-body]');
    if (!modal || !summary || !body) return;
    var items = cart && cart.items ? cart.items : [];
    if (!items.length) {
      summary.innerHTML = '<p>Your cart is currently empty.</p>';
      body.value = 'Save cart request: cart is currently empty.';
      return;
    }
    var lines = items.map(function(item) {
      return (item.quantity || 1) + ' × ' + (item.product_title || item.title) + ' - ' +
        (item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '') + ' - ' + (item.url || '');
    });
    summary.innerHTML = '<strong>Your cart summary</strong><ul>' + items.map(function(item) {
      return '<li>' + escapeHtml(item.quantity || 1) + ' × ' + escapeHtml(item.product_title || item.title) + '</li>';
    }).join('') + '</ul>';
    body.value = 'Please save this cart and send relevant follow-up offers.\n\nCart summary:\n' + lines.join('\n') +
      '\n\nCart total: ' + (cart.total_price != null ? (cart.total_price / 100).toFixed(2) : '') +
      '\n\nSource: Jaipur Handloom theme save-cart popup AE8';
  }

  function getCart() {
    if (JH.Cart && typeof JH.Cart.get === 'function') return JH.Cart.get();
    if (window.JHSmartCart && typeof window.JHSmartCart.get === 'function') return window.JHSmartCart.get();
    return Promise.reject(new Error('Cart owner is not available'));
  }

  function openSaveCart(opener) {
    var modal = qs('[data-jh-save-cart-modal]');
    if (!modal) return;
    state.saveCartOpener = opener || document.activeElement;
    getCart().then(updateSaveCartSummary).catch(function() {}).finally(function() {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      var email = qs('input[type="email"]', modal);
      if (A11y) A11y.activateDialog(modal, state.saveCartOpener, email || qs('[data-jh-save-cart-close]', modal));
    });
  }

  function closeSaveCart() {
    var modal = qs('[data-jh-save-cart-modal]');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    if (A11y) A11y.deactivateDialog(modal, state.saveCartOpener);
    state.saveCartOpener = null;
  }

  function bindSaveCart() {
    if (qs('[data-jh-saved-cart]')) return;
    if (state.saveCartBound) return;
    state.saveCartBound = true;
    document.addEventListener('click', function(event) {
      var open = event.target.closest && event.target.closest('[data-jh-save-cart-open]');
      if (open) {
        event.preventDefault();
        openSaveCart(open);
        return;
      }
      if (event.target.closest && event.target.closest('[data-jh-save-cart-close]')) {
        event.preventDefault();
        closeSaveCart();
      }
    });
    document.addEventListener('submit', function(event) {
      if (event.target && event.target.id === 'JHSaveCartForm') getCart().then(updateSaveCartSummary).catch(function() {});
    }, true);
    document.addEventListener('keydown', function(event) {
      var modal = qs('[data-jh-save-cart-modal]');
      if (!modal || modal.hidden) return;
      if (event.key === 'Escape') closeSaveCart();
      else if (event.key === 'Tab' && A11y) A11y.trapTab(event, modal);
    });
  }

  function addHeroVariant(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;
    var originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    var addOwner = JH.Cart && JH.Cart.addSilent;
    if (typeof addOwner !== 'function' && window.JHSmartCart) addOwner = window.JHSmartCart.addSilent;
    var addPromise = typeof addOwner === 'function' ?
      addOwner(variantId, 1) :
      Promise.reject(new Error('Cart owner is not available'));

    addPromise.then(function() {
      button.textContent = 'Added';
    }).catch(function() {
      button.textContent = originalText || 'Add to cart';
    }).finally(function() {
      window.setTimeout(function() {
        button.disabled = false;
        button.textContent = originalText || 'Add to cart';
      }, 1000);
    });
  }

  function bindHeroQuickAdd() {
    if (state.heroAddBound) return;
    state.heroAddBound = true;
    document.addEventListener('click', function(event) {
      var button = event.target.closest && event.target.closest('[data-jh-hero-quick-add]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      addHeroVariant(button);
    }, true);
  }

  function tidyHero(scope) {
    qsa('.jh-hero-gallery-v13134__slide > span', scope || document).forEach(function(element) {
      element.style.display = 'none';
    });
  }

  function init(scope) {
    initHeroRandomization(scope);
    initPools(scope);
    makeRowsAccessible(scope);
    improveLoadMore(scope);
    tuneImages(scope);
    tidyHero(scope);
    bindSaveCart();
    bindHeroQuickAdd();
    (U ? U.idle : function(callback) { window.setTimeout(callback, 180); })(function() {
      trimHiddenPools(scope);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { init(document); }, { once: true });
  } else init(document);

  document.addEventListener('shopify:section:load', function(event) {
    window.setTimeout(function() { init(event.target); }, 120);
  });

  JH.SaveCart = { open: openSaveCart, close: closeSaveCart };
})();
